import CircularProgress from '@mui/material/CircularProgress';
import { FiCalendar, FiClock } from 'react-icons/fi';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { isQaTesterRole } from '../utils/roleUtils';
import { departmentUsesBugTracker } from '../constants/departments';
import { buildMemberDashboardInsights } from '../utils/memberDashboardInsights';

const cardStyle = {
  background: '#fff',
  padding: '1rem 1.15rem',
  borderRadius: 12,
  border: '1px solid #e2e8f0',
  boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
};

const kpiLabel = { fontSize: '0.78rem', fontWeight: 600, color: '#64748b', marginBottom: 4 };
const kpiValue = { fontSize: '1.75rem', fontWeight: 800, color: '#1e293b', lineHeight: 1 };
const kpiSub = { fontSize: '0.72rem', color: '#94a3b8', marginTop: 4 };

/**
 * Compact sprint + project snapshot for manager profile modal (mirrors IC EmployeeDashboard).
 */
const MemberSprintOverview = ({ sprintData, memberRole, memberDepartment, stats, isLoading }) => {
  if (isLoading) {
    return (
      <div style={{ padding: '1.5rem 0', textAlign: 'center', color: '#64748b' }}>
        <CircularProgress size={28} thickness={4} sx={{ color: '#2C8FC7', mb: 1 }} />
        <div style={{ fontSize: '0.82rem', fontWeight: 600 }}>Loading sprint snapshot…</div>
      </div>
    );
  }

  if (!sprintData) return null;

  const {
    currentSprint,
    activeSprints = [],
    myWork = {},
    myOpenBugs = 0,
    teamProgress = {},
    blockers = {},
    projects = [],
    statusBreakdown = [],
  } = sprintData;

  const insights = buildMemberDashboardInsights({
    myWork,
    blockers,
    currentSprint,
    statusBreakdown,
    memberRole,
    stats,
    perspective: 'manager',
  });

  const {
    completionRate,
    blockerCount,
    criticalBlockers,
    confidenceScore,
    momentumMessage,
    focusRecommendations,
    myWorkMix,
  } = insights;

  const assignedCount = insights.assignedCount;
  const completedCount = insights.completedCount;
  const inProgressCount = insights.inProgressCount;
  const testingCount = insights.testingCount;
  const showBugKpi = departmentUsesBugTracker(memberDepartment)
    && (isQaTesterRole(memberRole) || myOpenBugs > 0);
  const otherActiveSprints = activeSprints.filter((s) => s.id !== currentSprint?.id);
  const timelineColor = currentSprint?.timelineStatus === 'onTrack' ? '#10b981' : '#ef4444';
  const closedThisWeek = Number(stats?.closed_this_week || 0);

  if (projects.length === 0) {
    return (
      <div style={{ ...cardStyle, marginBottom: '1.5rem', background: '#f8fafc', color: '#64748b', fontSize: '0.88rem' }}>
        No active projects assigned — sprint progress will appear once they join a project.
      </div>
    );
  }

  return (
    <div style={{ marginBottom: '1.75rem' }}>
      {currentSprint ? (
        <div style={{
          background: 'linear-gradient(135deg, #1272B3 0%, #2C8FC7 100%)',
          borderRadius: 14,
          padding: '1.25rem 1.35rem',
          color: '#fff',
          marginBottom: '1rem',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, fontSize: '0.78rem', opacity: 0.9 }}>
                <FiCalendar size={16} />
                Active sprint
              </div>
              <div style={{ fontSize: '1.15rem', fontWeight: 800, marginBottom: 2 }}>{currentSprint.name}</div>
              <div style={{ fontSize: '0.82rem', opacity: 0.9 }}>{currentSprint.projectName}</div>
            </div>
            <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: '0.72rem', opacity: 0.85 }}>Days left</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 800 }}>{currentSprint.daysRemaining}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.72rem', opacity: 0.85 }}>Team progress</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 800 }}>{currentSprint.progressActual}%</div>
                <div style={{ fontSize: '0.68rem', opacity: 0.8 }}>Expected {currentSprint.progressExpected}%</div>
              </div>
            </div>
          </div>
          <div style={{ marginTop: '0.85rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginBottom: 4 }}>
              <span>{teamProgress.completedPoints ?? 0} / {teamProgress.totalPoints ?? 0} story pts</span>
              <span style={{ fontWeight: 700, color: timelineColor === '#10b981' ? '#bbf7d0' : '#fecaca' }}>
                {currentSprint.timelineStatus === 'onTrack' ? 'On track' : 'Behind'}
              </span>
            </div>
            <div style={{ height: 8, background: 'rgba(255,255,255,0.2)', borderRadius: 999, overflow: 'hidden' }}>
              <div style={{
                width: `${Math.min(100, currentSprint.progressActual || 0)}%`,
                height: '100%',
                background: timelineColor,
                borderRadius: 999,
              }} />
            </div>
          </div>
        </div>
      ) : (
        <div style={{ ...cardStyle, marginBottom: '1rem', fontSize: '0.85rem', color: '#475569', lineHeight: 1.5 }}>
          Assigned to <strong>{projects.length}</strong> project{projects.length !== 1 ? 's' : ''}.
          {activeSprints.length > 0
            ? ' Active sprints are running — see project cards below.'
            : ' No active sprint is running in their projects right now.'}
        </div>
      )}

      {(otherActiveSprints.length > 0 || (!currentSprint && activeSprints.length > 0)) && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '0.65rem',
          marginBottom: '1rem',
        }}>
          {(currentSprint ? otherActiveSprints : activeSprints).map((sprint) => (
            <div key={sprint.id} style={{ ...cardStyle, padding: '0.85rem 1rem' }}>
              <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>
                {sprint.projectName}
              </div>
              <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#1e293b', marginBottom: 6 }}>{sprint.name}</div>
              <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                {sprint.daysRemaining} days left · {sprint.progressActual}% team progress
              </div>
              <div style={{ fontSize: '0.72rem', color: '#475569', marginTop: 4 }}>
                {sprint.myAssigned > 0
                  ? `${sprint.myAssigned} task${sprint.myAssigned !== 1 ? 's' : ''} assigned`
                  : 'No tasks assigned yet'}
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
        gap: '0.65rem',
        marginBottom: '1rem',
      }}>
        <div style={cardStyle}>
          <div style={kpiLabel}>Assigned work</div>
          <div style={kpiValue}>{assignedCount}</div>
          <div style={kpiSub}>
            {completedCount} done · {inProgressCount} in progress
            {testingCount > 0 ? ` · ${testingCount} in review` : ''}
          </div>
        </div>
        <div style={cardStyle}>
          <div style={kpiLabel}>Completion</div>
          <div style={kpiValue}>{completionRate}%</div>
          <div style={kpiSub}>{currentSprint ? 'Active sprint' : 'All projects'}</div>
        </div>
        <div style={cardStyle}>
          <div style={kpiLabel}>Projects</div>
          <div style={kpiValue}>{projects.length}</div>
          <div style={kpiSub}>{activeSprints.length} active sprint{activeSprints.length !== 1 ? 's' : ''}</div>
        </div>
        {showBugKpi && (
          <div style={cardStyle}>
            <div style={kpiLabel}>Open bugs</div>
            <div style={{ ...kpiValue, color: myOpenBugs > 0 ? '#dc2626' : '#10b981' }}>{myOpenBugs}</div>
            <div style={kpiSub}>Assigned to them</div>
          </div>
        )}
        <div style={cardStyle}>
          <div style={kpiLabel}>Blockers</div>
          <div style={{ ...kpiValue, color: blockerCount > 0 ? '#dc2626' : '#10b981' }}>
            {blockerCount}
          </div>
          <div style={kpiSub}>{criticalBlockers} critical · assigned to them</div>
        </div>
        {closedThisWeek > 0 && (
          <div style={cardStyle}>
            <div style={kpiLabel}>Closed this week</div>
            <div style={kpiValue}>{closedThisWeek}</div>
            <div style={kpiSub}>All work types</div>
          </div>
        )}
      </div>

      {projects.length > 0 && (
        <>
          <h4 style={{ fontSize: '0.92rem', fontWeight: 700, color: '#1e293b', margin: '0 0 0.65rem' }}>
            Projects &amp; sprint progress
          </h4>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '0.65rem',
            marginBottom: '0.5rem',
          }}>
            {projects.map((project) => (
              <div key={project.id} style={{ ...cardStyle, padding: '0.85rem 1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                  <div style={{ fontWeight: 700, color: '#1e293b', fontSize: '0.88rem' }}>{project.name}</div>
                  <span style={{
                    fontSize: '0.62rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    padding: '2px 6px',
                    borderRadius: 999,
                    background: project.status === 'Active' ? '#dcfce7' : '#f1f5f9',
                    color: project.status === 'Active' ? '#166534' : '#64748b',
                  }}>
                    {project.status || 'Unknown'}
                  </span>
                </div>
                <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: 8 }}>
                  {project.activeSprints > 0 ? (
                    <>
                      {project.completedIssues} / {project.totalIssues} active sprint tasks done
                      {project.activeSprintName && (
                        <span style={{ display: 'block', marginTop: 3, color: '#475569', fontWeight: 600 }}>
                          {project.activeSprintName}
                        </span>
                      )}
                    </>
                  ) : (
                    'No active sprint running'
                  )}
                </div>
                <div style={{ height: 6, background: '#f1f5f9', borderRadius: 999, overflow: 'hidden' }}>
                  <div style={{
                    width: `${project.activeSprints > 0 ? project.progress : 0}%`,
                    height: '100%',
                    background: project.progress >= 70 ? '#10b981' : '#2C8FC7',
                    opacity: project.activeSprints > 0 ? 1 : 0.35,
                  }} />
                </div>
                <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 4, fontWeight: 600 }}>
                  {project.activeSprints > 0 ? `${project.progress}% sprint complete` : '—'}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '0.85rem',
        marginBottom: '1rem',
      }}>
        <div style={{ ...cardStyle, padding: '1rem 1.15rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem', marginBottom: '0.75rem' }}>
            <div>
              <h4 style={{ margin: 0, fontSize: '0.92rem', fontWeight: 700, color: '#1e293b' }}>
                Smart Review
              </h4>
              <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 4 }}>
                Guidance from sprint progress and role focus
              </div>
            </div>
            <div style={{
              padding: '0.3rem 0.55rem',
              borderRadius: 999,
              background: confidenceScore >= 70 ? '#dcfce7' : '#fef3c7',
              color: confidenceScore >= 70 ? '#166534' : '#92400e',
              fontWeight: 700,
              fontSize: '0.72rem',
              whiteSpace: 'nowrap',
            }}>
              Confidence {confidenceScore}%
            </div>
          </div>
          <div style={{
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: 10,
            padding: '0.75rem',
            marginBottom: '0.75rem',
            color: '#334155',
            fontWeight: 600,
            fontSize: '0.82rem',
            lineHeight: 1.45,
          }}>
            {momentumMessage}
          </div>
          <div style={{ display: 'grid', gap: '0.45rem' }}>
            {focusRecommendations.map((item, idx) => (
              <div key={idx} style={{ display: 'flex', gap: 8, fontSize: '0.78rem', color: '#475569', lineHeight: 1.4 }}>
                <span style={{ color: '#1272B3', fontWeight: 800 }}>•</span>
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ ...cardStyle, padding: '1rem 1.15rem' }}>
          <h4 style={{ margin: 0, fontSize: '0.92rem', fontWeight: 700, color: '#1e293b' }}>
            Workload Snapshot
          </h4>
          <div style={{ fontSize: '0.72rem', color: '#64748b', margin: '4px 0 8px' }}>
            Completed, active, and pending work in active sprint
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, color: '#64748b', fontSize: '0.75rem' }}>
            <FiClock size={14} />
            Completion rate: {completionRate}%
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={myWorkMix} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {myWorkMix.map((entry) => <Cell key={entry.label} fill={entry.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div style={{ borderTop: '1px solid #e2e8f0', marginTop: '1.25rem', paddingTop: '0.25rem' }} />
    </div>
  );
};

export default MemberSprintOverview;
